import React from 'react'
import { Redirect, RouteComponentProps, useParams } from 'react-router-dom'

import { ErrorMessage } from '@sketch/components'
import {
  RouteParams,
  getVersionRenderStatus,
  useQueryParams,
} from '@sketch/modules-common'
import PageCanvasViewHeader from './components/PageCanvasViewHeader'

import {
  isUpgradeToLatestNeeded,
  UpgradeToLatestVersion,
  useVersioning,
} from '../../versioning'
import { getPermanentPageId } from '../utils'

import { useGetVersionQueryWithPolling } from '../operations'
import { ReactComponent as DocumentIcon } from '@sketch/icons/document-64'

import { ErrorWrapper } from './PageCanvasView.styles'
import { DocumentSidebarLayoutExtraProps } from '../components/DocumentSidebarLayout'

import { Panel } from '../components/Panel'
import { FloatingZoom } from '../FrameView/components/FloatingZoom'
import { CanvasLeftPanelPortal } from '../../canvas/components/CanvasLeftPanel'
import {
  CanvasMobileToolbar,
  PAGE_CANVAS_TOOLBAR_ITEMS,
} from '../components/CanvasMobileToolbar'
import { PageSelector } from './components/PageSelector'
import { useGetWorkspaceQuery } from '@sketch/gql-types'

// Params are `Partial` because one Route serves all the canvas templates, so
// which ones are present depends on the URL. Only `location` is read from here
// anyway; the params this view needs come from `useParams` below.
type PageCanvasViewProps = RouteComponentProps<
  Partial<RouteParams<'SHARE_VIEW' | 'SHARE_PAGE_CANVAS_VIEW'>>
> &
  DocumentSidebarLayoutExtraProps

export default function PageCanvasView(props: PageCanvasViewProps) {
  const {
    HeaderPortal,
    SidebarRightPortal,
    setSidebarLeftOpen,
    toggleSidebarRight,
    isSidebarRightOpen,
  } = props

  const queryParams = useQueryParams<'SHARE_PAGE_CANVAS_VIEW' | 'SHARE_VIEW'>()
  const params = useParams<
    RouteParams<'SHARE_PAGE_CANVAS_VIEW'> | RouteParams<'SHARE_VIEW'>
  >()

  const versioning = useVersioning()
  const { share, versionShortId, isViewingLatestVersion, hasPendingPatches } =
    versioning

  const { data: workspaceData } = useGetWorkspaceQuery({
    variables: { identifier: share.workspace.identifier },
    fetchPolicy: 'cache-first',
    // Skip for non-members to avoid unauthorized GraphQL errors
    skip: !share.workspace.userIsMember,
  })

  const isWorkspaceEncrypted =
    workspaceData?.workspace.features.encryptionEnabled ?? false

  const currentVersion = !versioning.loading ? versioning.currentVersion : null

  const { data, error } = useGetVersionQueryWithPolling({
    variables: {
      shareIdentifier: share.identifier,
      versionShortId,
    },
  })

  const permanentPageIdURL = 'pageUUID' in params ? params.pageUUID : undefined
  const firstDocumentPageIdentifier =
    currentVersion?.document?.pages?.entries[0]?.uuid

  const permanentPageId =
    getPermanentPageId(permanentPageIdURL, currentVersion) || ''

  /**
   * We should redirect to the SHARE_VIEW when:
   * - The pageUUID matches the permanent identifier of the first page (Consistency)
   * - There's a search query arg and we need to show the results grid
   */
  const shouldRedirectToShareView =
    (firstDocumentPageIdentifier &&
      permanentPageIdURL &&
      permanentPageIdURL === firstDocumentPageIdentifier) ||
    'search' in queryParams

  if (isUpgradeToLatestNeeded(error)) {
    return <UpgradeToLatestVersion error={error} />
  }

  if (shouldRedirectToShareView) {
    const pathname = versioning.getPathname({
      routeKey: 'SHARE_VIEW',
      routeParams: {
        shareID: versioning.share.identifier,
      },
    })

    return <Redirect to={{ ...props.location, pathname }} />
  }

  const parsedRenderStatus = getVersionRenderStatus(data?.share?.version)

  const isCurrentVersionStarred = currentVersion?.kind === 'PUBLISHED'

  let canvasContent = <></>

  if (permanentPageId) {
    // Whatever stands where the canvas goes - the canvas itself, the wait for
    // the presentation file, or the guard for a document that is not ready -
    // is mounted once by ShareCanvasRoute, so there is only ever one of them.
    // This is the page's own half of the left panel: a portal, so it finds no
    // host and renders nothing while something else is standing there.
    canvasContent = (
      <CanvasLeftPanelPortal>
        <PageSelector
          shareIdentifier={share.identifier}
          versionShortId={versionShortId}
          permanentPageIdentifier={permanentPageId!}
        />
      </CanvasLeftPanelPortal>
    )
  } else if (parsedRenderStatus === 'ready') {
    // A finished document with nowhere to navigate to. Only worth saying once
    // the document is finished: until then the guard above is already
    // explaining why there is nothing here.
    canvasContent = (
      <ErrorWrapper>
        <ErrorMessage
          title="No pages in document"
          icon={<DocumentIcon />}
          iconSize="medium"
        >
          No pages have been added here yet.
        </ErrorMessage>
      </ErrorWrapper>
    )
  }

  // The canvas and everything it needs is mounted once by ShareCanvasRoute,
  // above this and the other two views, so that moving between them does not
  // take the renderer with it.
  return (
    <>
      <HeaderPortal>
        <PageCanvasViewHeader
          share={share}
          setSidebarLeftOpen={setSidebarLeftOpen}
          toggleSidebarRight={toggleSidebarRight}
          isSidebarRightOpen={isSidebarRightOpen}
          isViewingLatestVersion={isViewingLatestVersion}
          isViewingStarredVersion={isCurrentVersionStarred}
          hasPendingPatches={hasPendingPatches}
          userCanOpenInApp={!!currentVersion?.document?.userCanOpenInApp}
          currentVersion={currentVersion || undefined}
          hideSearch={isWorkspaceEncrypted}
        />

        <Panel
          share={share}
          version={currentVersion || undefined}
          userCanSeeComments
          userCanInspect={share.userCanInspect}
          SidebarRightPortal={SidebarRightPortal}
        />
      </HeaderPortal>

      <FloatingZoom />

      {canvasContent}
      <CanvasMobileToolbar
        items={PAGE_CANVAS_TOOLBAR_ITEMS}
        hideSearch={isWorkspaceEncrypted}
      />
    </>
  )
}
